// ============================================
// BOÎTES MAIL — AIDE MICROSOFT 365 (ENTRA ID)
// ============================================
// Pas à pas pour créer l'application qui lit les boîtes sav@ et assistance@,
// et qui envoie les e-mails du SAV depuis la boîte d'envoi (Mail.Send).
// Les libellés reprennent ceux du portail Entra en français.

import React from 'react';

const ETAPES: Array<{ titre: string; contenu: React.ReactNode }> = [
  {
    titre: 'Ouvrir le centre d’administration Microsoft Entra',
    contenu: (
      <>
        Allez sur <span className="font-medium text-encre">entra.microsoft.com</span> avec un compte
        administrateur (administrateur global ou administrateur d’applications).
      </>
    ),
  },
  {
    titre: 'Inscrire une application',
    contenu: (
      <>
        « Identité » → « Applications » → « Inscriptions d’applications » → « Nouvelle inscription ».
        Nom : par exemple <em>SAV KLY – lecture des boîtes</em>. Types de comptes : « Comptes dans cet
        annuaire d’organisation uniquement ». Aucune URI de redirection. Cliquez « S’inscrire ».
      </>
    ),
  },
  {
    titre: 'Relever les deux identifiants',
    contenu: (
      <>
        Sur la page « Vue d’ensemble » de l’application, copiez l’« ID d’application (client) » dans le
        champ <strong>Identifiant de l’application</strong>, et l’« ID de l’annuaire (locataire) » dans le
        champ <strong>Tenant Azure AD</strong>.
      </>
    ),
  },
  {
    titre: 'Créer un secret client',
    contenu: (
      <>
        « Certificats et secrets » → « Nouveau secret client ». Copiez tout de suite la colonne
        « Valeur » (pas l’« ID du secret ») : Microsoft ne la réaffiche plus. Collez-la dans le champ
        <strong> Secret client</strong>. Notez la date d’expiration : à cette date la boîte passera en
        erreur et il faudra saisir un nouveau secret.
      </>
    ),
  },
  {
    titre: 'Donner les droits de lire, classer et envoyer les e-mails',
    contenu: (
      <>
        « Autorisations de l’API » → « Ajouter une autorisation » → « Microsoft Graph » →
        « Autorisations d’application » (et non « déléguées ») → cochez les trois droits :
        <code className="mx-0.5">Mail.Read</code> pour lire la boîte et suivre les nouveaux messages
        (webhook), <code className="mx-0.5">Mail.ReadWrite</code> pour marquer comme lus les e-mails
        devenus des dossiers, et <code className="mx-0.5">Mail.Send</code> pour répondre aux clients et
        envoyer les e-mails du SAV depuis la boîte. Cliquez ensuite « Accorder un consentement
        d’administrateur » : la colonne « État » doit afficher « Accordé » pour les trois. Sans
        <code className="mx-0.5">Mail.Send</code>, la boîte est lue mais ne peut pas envoyer.
      </>
    ),
  },
  {
    titre: 'Restreindre l’application aux seules boîtes du SAV (recommandé)',
    contenu: (
      <>
        Sans restriction, ces droits d’application valent pour toutes les boîtes de l’entreprise :
        lire, et aussi envoyer au nom de n’importe qui. Dans Exchange Online PowerShell, créez une
        stratégie d’accès limitée à un groupe de sécurité à extension messagerie contenant sav@ et
        assistance@. Elle restreint la lecture comme l’envoi : la boîte d’envoi doit faire partie du
        groupe.
        <code className="mt-1 block whitespace-pre-wrap break-all rounded-lg bg-brume px-3 py-2 text-[11px] text-encre">
          New-ApplicationAccessPolicy -AppId &lt;ID d’application&gt; -PolicyScopeGroupId &lt;groupe des boîtes SAV&gt; -AccessRight RestrictAccess -Description "SAV KLY"
        </code>
        Pour vérifier une boîte (la prise en compte peut demander jusqu’à une heure) :
        <code className="mt-1 block whitespace-pre-wrap break-all rounded-lg bg-brume px-3 py-2 text-[11px] text-encre">
          Test-ApplicationAccessPolicy -AppId &lt;ID d’application&gt; -Identity sav@klygroupe.com
        </code>
      </>
    ),
  },
  {
    titre: 'Déclarer la boîte ici',
    contenu: (
      <>
        « Ajouter une boîte », collez les trois valeurs, enregistrez, puis « Tester la connexion ». Quand
        le test réussit, cliquez « Activer » : seuls les e-mails reçus à partir de ce moment deviendront
        des dossiers.
      </>
    ),
  },
  {
    titre: 'Choisir la boîte d’envoi',
    contenu: (
      <>
        Une fois la boîte du SAV activée (lue), activez « Envoyer depuis cette boîte », puis « Envoyer
        un e-mail de test ». Une boîte suspendue ne peut pas envoyer : les clients répondent à l’adresse
        d’envoi, et leurs réponses doivent être lues.
        Les accusés de réception, réponses aux clients et relances partent alors de cette adresse par
        Microsoft 365, et une réponse reste dans la conversation du client. Une seule boîte peut être
        la boîte d’envoi. Sans boîte d’envoi, les e-mails passent par le serveur SMTP s’il est configuré.
      </>
    ),
  },
  {
    titre: 'Temps réel (webhook) — côté serveur',
    contenu: (
      <>
        Pour qu’un e-mail crée un dossier en quelques secondes, l’équipe technique renseigne sur le
        serveur l’adresse publique du webhook (<code className="mx-0.5">GRAPH_WEBHOOK_URL</code>, qui se
        termine par <code className="mx-0.5">/api/webhooks/graph</code>) et son secret
        (<code className="mx-0.5">GRAPH_WEBHOOK_SECRET</code>). L’abonnement est ensuite créé et
        renouvelé automatiquement (Microsoft le limite à trois jours). Sans webhook, la boîte reste lue
        par le cycle de rattrapage réglé dans Paramètres.
      </>
    ),
  },
];

const GuideMicrosoft: React.FC = () => (
  <ol className="space-y-3">
    {ETAPES.map((e, i) => (
      <li key={e.titre} className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brume text-xs font-semibold text-encre">
          {i + 1}
        </span>
        <div className="min-w-0 text-sm text-graphite">
          <p className="font-medium text-encre">{e.titre}</p>
          <p className="mt-0.5">{e.contenu}</p>
        </div>
      </li>
    ))}
  </ol>
);

export default GuideMicrosoft;
